/**
 * Professionisti sul telefono (7/10/2026): risponde a "chi lavora con noi, e
 * quanto ci è costato?". È una rubrica: si cerca una persona per chiamarla o
 * per vedere cosa le abbiamo pagato.
 *
 * Prima, a 390px: una frase di tre righe sotto il titolo, quattro numeri in
 * griglia (la tariffa media "Riservato", "Anche clienti 0"), "Cerca, ordina
 * e filtra" da solo su una riga, poi schede CITTÀ / FATTURE / SPESO / STATO
 * alte mezzo schermo, uguali a quelle dei contatti.
 * Qui:
 *   1. in grande quante persone e quanto ci sono costate in tutto;
 *   2. tre modi di guardarle (dalla A alla Z, le più recenti, quelle che
 *      costano di più), la ricerca e i filtri dietro un tasto;
 *   3. una riga per persona: dove sta, l'ultima fattura, quanto le abbiamo
 *      pagato a destra; dalla A alla Z con le lettere, come la rubrica.
 * Stesse chiamate e stessi parametri nell'indirizzo del computer (q, ordina,
 * stato, citta). Le società fornitrici restano in Amministrazione.
 */
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, euro } from '../../lib/utils';
import { adminLinks } from '../../lib/adminRoutes';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { MEmpty, MGroup, MHero, MPage, MRow, MSearch, MSeg, iniziali, leggibile } from '../../components/mobile/MobileKit';
import './suppliers-mobile.css';

interface Supplier {
  id: string; fullName: string; vat: string | null; email: string | null; city: string | null;
  hourlyRate: string | null; active: boolean; skills: { name: string; level: number }[];
  isPerson: boolean;
  alsoClient: { id: string; shortName: string } | null;
  payablesCount: number;
  payablesTotal: number;
  lastPayableDate: string | null;
}

/** Gli stessi ordini del computer (li fa l'API). */
const ORDINI: OpzioneOrdina[] = [
  { key: 'nome', label: 'Nome dalla A alla Z, prima gli attivi' },
  { key: 'costo', label: 'Prima chi ci è costato di più' },
  { key: 'fatture', label: 'Prima chi ci ha mandato più fatture' },
  { key: 'ultima', label: 'Prima chi ci ha fatturato più di recente' },
  { key: 'citta', label: 'Città dalla A alla Z' },
];

/** Come sul computer: "CATANIA" e "Catania" sono la stessa città. */
function cittaDellAnagrafica(items: Supplier[]): Array<{ value: string; label: string; count: number }> {
  const gruppi = new Map<string, { nomi: string[]; count: number }>();
  for (const s of items) {
    const c = s.city?.trim();
    if (!c) continue;
    const k = c.toLocaleLowerCase('it');
    const g = gruppi.get(k) ?? { nomi: [], count: 0 };
    g.nomi.push(c);
    g.count += 1;
    gruppi.set(k, g);
  }
  const bello = (nomi: string[]) => nomi.find((n) => n !== n.toLocaleUpperCase('it'))
    ?? nomi[0]!.toLocaleLowerCase('it').replace(/(^|[\s'’-])(\p{L})/gu, (_m, sep: string, l: string) => sep + l.toLocaleUpperCase('it'));
  return [...gruppi.values()]
    .map((g) => ({ value: bello(g.nomi), label: bello(g.nomi), count: g.count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'it'));
}

const giorno = (iso: string) => {
  const d = new Date(iso);
  const stessoAnno = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', ...(stessoAnno ? {} : { year: 'numeric' }) }).replace('.', '');
};

type Vista = 'nome' | 'ultima' | 'costo';

export function SuppliersMobile() {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [debSearch, setDebSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  // La lista intera: per i conti in cima e per le città dei filtri (stessa chiave del computer).
  const { data: allData } = useQuery({
    queryKey: ['suppliers', 'stats'],
    queryFn: () => api.get<{ items: Supplier[]; total: number }>('/suppliers?pageSize=200'),
  });

  const ordina = useOrdina(ORDINI);
  const tutti = allData?.items;
  const defsFiltri = useMemo<FiltroDef[]>(() => {
    const lista = tutti ?? [];
    const conta = (f: (s: Supplier) => boolean) => (tutti ? lista.filter(f).length : undefined);
    return [
      {
        param: 'stato', label: 'Stato della scheda',
        opzioni: [
          { value: 'attivi', label: 'Solo i professionisti attivi', count: conta((s) => s.active) },
          { value: 'non-attivi', label: 'Solo i professionisti non più attivi', count: conta((s) => !s.active) },
        ],
      },
      { param: 'citta', label: 'In che città sta', multi: true, opzioni: cittaDellAnagrafica(lista) },
    ];
  }, [tutti]);
  const filtri = useFiltri(defsFiltri);
  const stato = filtri.valore('stato');
  const citta = filtri.valori.citta ?? [];

  // Una pagina sola da 200 (sono qualche decina): sul telefono non si sfoglia.
  const params = new URLSearchParams();
  if (debSearch) params.set('search', debSearch);
  if (!ordina.predefinita) params.set('sort', ordina.key);
  if (stato) params.set('stato', stato);
  if (citta.length) params.set('citta', citta.join(','));
  params.set('page', '1');
  params.set('pageSize', '200');
  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', debSearch, ordina.key, filtri.chiave, 'telefono'],
    queryFn: () => api.get<{ items: Supplier[]; total: number }>(`/suppliers?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  const items = data?.items ?? [];

  const persone = allData?.items ?? [];
  const attivi = persone.filter((s) => s.active).length;
  const speso = persone.reduce((a, s) => a + (s.payablesTotal || 0), 0);
  const fatture = persone.reduce((a, s) => a + (s.payablesCount || 0), 0);

  const vista: Vista | '' = (['nome', 'ultima', 'costo'] as string[]).includes(ordina.key) ? (ordina.key as Vista) : '';

  const riga = (s: Supplier) => {
    const nome = leggibile(s.fullName);
    return (
      <MRow
        key={s.id}
        text={iniziali(nome)}
        round
        title={nome}
        sub={[
          s.city ? leggibile(s.city) : null,
          s.payablesCount ? `${s.payablesCount} ${s.payablesCount === 1 ? 'fattura' : 'fatture'}` : 'nessuna fattura',
          s.active ? null : 'non più attivo',
          s.alsoClient ? 'anche cliente' : null,
        ].filter(Boolean).join(' · ')}
        // A destra quanto le abbiamo pagato e quando è arrivata l'ultima fattura.
        trail={s.payablesTotal ? `${euro(s.payablesTotal)}` : undefined}
        trailSub={s.lastPayableDate ? giorno(s.lastPayableDate) : undefined}
        onClick={() => navigate(`/suppliers/${s.id}`)}
      />
    );
  };

  return (
    <div className="spm">
      <MPage>
        <MHero
          label={attivi === 1 ? 'Professionista attivo' : 'Professionisti attivi'}
          value={allData ? attivi : '…'}
          sub={speso > 0 ? `ci sono costati ${euro(speso)} in tutto, con ${fatture} fatture` : undefined}
        />

        <div className="spm-tools-wrap">
          <MSeg<Vista | ''>
            value={vista}
            onChange={(k) => k && ordina.set(k)}
            options={[
              { key: 'nome', label: 'Dalla A alla Z' },
              { key: 'ultima', label: 'Più recenti' },
              { key: 'costo', label: 'Costano di più' },
            ]}
          />
          <div className="mk-tools spm-tools">
            <MSearch value={search} onChange={setSearch} placeholder="Cerca un nome o una città" />
            <OrdinaFiltra filtri={filtri} label="Filtra" />
          </div>
        </div>

        {isLoading && !data ? (
          <MGroup title="Professionisti"><MEmpty>Carico i professionisti…</MEmpty></MGroup>
        ) : !items.length ? (
          <MGroup title="Professionisti">
            <MEmpty>{search.trim() || filtri.attivi ? 'Nessun professionista con questa ricerca o con questi filtri.' : 'Nessun professionista: arrivano da FattureInCloud con le loro fatture.'}</MEmpty>
          </MGroup>
        ) : (
          <MGroup
            title={vista === 'costo' ? 'Da chi ci è costato di più' : vista === 'ultima' ? "Dall'ultima fattura arrivata" : vista === 'nome' ? 'Dalla A alla Z' : 'Professionisti'}
            count={items.length}
          >
            {items.map(riga)}
          </MGroup>
        )}

        <MGroup title="Società fornitrici">
          <MRow icon="building" title="Le società che ci fatturano" sub="stanno in Amministrazione, nelle spese" onClick={() => navigate(adminLinks.fornitori())} />
        </MGroup>

        <DesktopOnly
          cosa="Aggiungere un professionista o esportare l'elenco"
          perche="servono anagrafica fiscale, competenze e tariffe. Dal telefono si cerca, si chiama e si allegano documenti."
        ><></></DesktopOnly>
      </MPage>
    </div>
  );
}
